import {Component, Injector, OnInit} from '@angular/core';
import {AppCoreModule} from "@modules/app.core.module";
import {FlexModule} from "@angular/flex-layout";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {FeatureSpacesModule} from "@modules/landlord-portal/features/feature-spaces.module";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatIcon} from "@angular/material/icon";
import {CommonModule} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {ManageIndustriesService} from "../manage-industries.service";
import {SelectableCategory} from "../industry.model";
import {IndustryCategoryCardComponent} from "../industry-category-card/industry-category-card.component";
import {AddCategoryDialogComponent} from "../add-category-dialog/add-category-dialog.component";
import {AddIndustryDialogComponent} from "../add-industry-dialog/add-industry-dialog.component";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatTooltip} from "@angular/material/tooltip";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {ControlType} from "@features/leads/application-form-builder/add-questions/question-components.model";
import {ExpandingInfoPanelComponent} from "@shared-components/expanding-info-panel/expanding-info-panel.component";

@Component({
  selector: 'app-manage-industries',
  standalone: true,
  imports: [
    AppCoreModule,
    FlexModule,
    FeatureSpacesModule,
    MatButton,
    MatDivider,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    CommonModule,
    FormsModule,
    IndustryCategoryCardComponent,
    MatIconButton,
    MatProgressSpinner,
    MatTooltip,
    ReactiveFormsModule,
    ExpandingInfoPanelComponent
  ],
  templateUrl: './manage-industries.component.html',
  styleUrl: './manage-industries.component.scss'
})
export class ManageIndustriesComponent extends BasePageComponent implements OnInit {
  moreInfoMessage: boolean = false;
  protected readonly shortInfoMessage = "";
  protected readonly longInfoMessage = "";
  protected readonly PanelType = PanelType;
  protected readonly ControlType = ControlType;


  categories: SelectableCategory[] = [];
  originalCategories: SelectableCategory[] = [];
  filteredCategories: SelectableCategory[] = [];
  searchTerm: string = '';
  isDirty: boolean = false;

  constructor(
    injector: Injector,
    public utils: UtilsService,
    public loader: LoadingService,
    private industriesService: ManageIndustriesService,
    private dialog: MatDialog
  ) {
    super(injector);
  }

  ngOnInit() {
    this.loadCategories();
  }

  protected get offeringId(): string {
    return this.offering?.offeringUuid!;
  }

  get totalSelected(): number {
    return this.categories.reduce((acc, cat) =>
      acc + cat.industries.filter(ind => ind.selected).length, 0);
  }

  private loadCategories() {
    this.loader.show();
    this.industriesService.getAllSelectableOptionsForOffering().subscribe({
      next: (categories) => {
        this.categories = categories;
        this.originalCategories = JSON.parse(JSON.stringify(categories));
        this.applyFilter();
        this.loader.hide();
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.loader.hide();
      }
    });
  }

  onSearchChange() {
    this.applyFilter();
  }

  clearSearch() {
    this.searchTerm = '';
    this.applyFilter();
  }

  private applyFilter() {
    if (!this.searchTerm.trim()) {
      this.filteredCategories = [...this.categories];
      return;
    }

    const term = this.searchTerm.toLowerCase();
    this.filteredCategories = this.categories.filter(category => {
      const categoryMatch =
        category.name.toLowerCase().includes(term) ||
        (category.labelNameOverride?.toLowerCase().includes(term)) ||
        (category.description?.toLowerCase().includes(term)) ||
        (category.labelDescriptionOverride?.toLowerCase().includes(term));

      const industryMatch = category.industries.some(industry =>
        industry.name.toLowerCase().includes(term) ||
        (industry.labelNameOverride?.toLowerCase().includes(term)) ||
        (industry.description?.toLowerCase().includes(term)) ||
        (industry.labelDescriptionOverride?.toLowerCase().includes(term))
      );

      return categoryMatch || industryMatch;
    });
  }

  onCategoryChanged(updatedCategory: SelectableCategory) {
    const index = this.categories.findIndex(cat => cat.id === updatedCategory.id);
    if (index !== -1) {
      this.categories[index] = updatedCategory;
      this.checkDirtyState();
      this.applyFilter();
    }
  }

  private checkDirtyState() {
    this.isDirty = JSON.stringify(this.categories) !== JSON.stringify(this.originalCategories);
  }

  protected addNewCategory() {
    const dialogRef = this.dialog.open(AddCategoryDialogComponent, {
      width: '500px',
      data: {
        offeringUuid: this.offeringId,
        existingCodes: this.categories.map(cat => cat.code)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.categories.push(result);
        this.checkDirtyState();
        this.applyFilter();
      }
    });
  }

  private getAllIndustryCodes(): string[] {
    const codes: string[] = [];
    this.categories.forEach(cat => {
      cat.industries.forEach(ind => codes.push(ind.code));
    });
    return codes;
  }

  save() {
    this.loader.show();
    this.industriesService.saveOfferingConfig(this.categories).subscribe({
      next: (savedCategories) => {
        this.categories = savedCategories;
        this.originalCategories = JSON.parse(JSON.stringify(savedCategories));
        this.isDirty = false;
        this.applyFilter();
        this.loader.hide();
        // todo Show success message
      },
      error: (error) => {
        console.error('Error saving configuration:', error);
        this.loader.hide();
        // todo Show error message
      }
    });
  }

  cancel() {
    this.categories = JSON.parse(JSON.stringify(this.originalCategories));
    this.isDirty = false;
    this.applyFilter();
  }

  trackByCategoryId(index: number, category: SelectableCategory): any {
    return category.id || category.code;
  }

  onAddIndustryToCategory(categoryId: number) {
    const dialogRef = this.dialog.open(AddIndustryDialogComponent, {
      width: '500px',
      data: {
        offeringUuid: this.offeringId,
        categories: this.categories.filter(cat => cat.id === categoryId), // Pre-select this category
        existingCodes: this.getAllIndustryCodes()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const category = this.categories.find(cat => cat.id === result.categoryId);
        if (category) {
          category.industries.push(result.industry);
          this.checkDirtyState();
          this.applyFilter();
        }
      }
    });
  }
}
